import React from "react";
import {
    Database,
    RefreshCw,
    CheckCircle,
    Loader,
    AlertCircle,
    XCircle,
    ArrowLeft
} from "lucide-react";
import axios from "../../../config/axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const ProgressStep = ({ title, description, status, icon }) => {
    const getStatusStyles = () => {
        switch (status) {
            case "complete":
                return {
                    iconBg: "bg-green-100",
                    iconColor: "text-green-600",
                    titleColor: "text-gray-900",
                    descColor: "text-gray-600",
                    checkIcon: true,
                };
            case "active":
                return {
                    iconBg: "bg-primary-100",
                    iconColor: "text-primary-600 animate-pulse",
                    titleColor: "text-gray-900 font-semibold",
                    descColor: "text-gray-700",
                    checkIcon: false,
                };
            case "pending":
            default:
                return {
                    iconBg: "bg-gray-100",
                    iconColor: "text-gray-400",
                    titleColor: "text-gray-500",
                    descColor: "text-gray-400",
                    checkIcon: false,
                };
        }
    };

    const styles = getStatusStyles();

    return (
        <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}>
                {styles.checkIcon ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                    <div className={styles.iconColor}>{icon}</div>
                )}
            </div>
            <div className="flex-1 pt-1">
                <h4 className={`text-sm font-medium ${styles.titleColor}`}>
                    {title}
                </h4>
                <p className={`text-xs ${styles.descColor} mt-0.5`}>
                    {description}
                </p>
            </div>
            {status === "active" && (
                <Loader className="w-5 h-5 text-primary-600 animate-spin mt-2" />
            )}
        </div>
    );
};

export const SetupProgress = ({
    dataSource,
    pollingWorkflow,
    retrying,
    instanceId,
    setRetrying,
    setPollingWorkflow,
    pollWorkflowStatus,
    connectorName
}) => {
    const navigate = useNavigate();

    // Returns true if content was rendered, false if parent should continue
    // This layout is slightly different than normal component pattern to match original
    // logic flow, but here we just render the specific states.

    if (dataSource?.connectionStatus === "setting_up" || pollingWorkflow || retrying) {
        return (
            <div className="max-w-4xl mx-auto py-16 px-4">
                <div className="bg-white rounded-lg shadow-lg p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Loader className="w-8 h-8 text-primary-600 animate-spin" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Setting Up Your Connection
                        </h2>
                        <p className="text-gray-600">
                            Please wait while we configure your data pipeline
                        </p>
                    </div>

                    {/* Progress Steps */}
                    <div className="space-y-4 max-w-lg mx-auto">
                        <ProgressStep
                            title="Creating BigQuery Dataset"
                            description="Preparing your data warehouse"
                            status="complete"
                            icon={<Database className="w-5 h-5" />}
                        />
                        <ProgressStep
                            title="Configuring Airbyte Destination"
                            description="Setting up data sync target"
                            status="complete"
                            icon={<Database className="w-5 h-5" />}
                        />
                        <ProgressStep
                            title="Establishing Connection"
                            description="Connecting source to destination"
                            status="active"
                            icon={<RefreshCw className="w-5 h-5" />}
                        />
                        <ProgressStep
                            title="Finalizing Setup"
                            description="Completing configuration"
                            status="pending"
                            icon={<CheckCircle className="w-5 h-5" />}
                        />
                    </div>

                    {/* Info Box */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">This usually takes 30-60 seconds</p>
                                <p>You'll be notified once the setup is complete and your first sync begins.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Workflow failed state
    if (
        dataSource?.connectionStatus === "failed" ||
        (!dataSource?.airbyteConnectionId && dataSource?.connectionError)
    ) {
        return (
            <div className="max-w-4xl mx-auto py-16 px-4">
                <div className="bg-white rounded-lg shadow-lg p-8">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            Connection Setup Failed
                        </h2>
                        <p className="text-gray-600 mb-6">
                            There was a problem setting up your {connectorName || "connector"} connection.
                        </p>

                        {/* Error Details */}
                        {dataSource?.connectionError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
                                <div className="flex items-start text-left">
                                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium mb-1">Error Details:</p>
                                        <p className="font-mono text-xs bg-red-100 p-2 rounded mt-2">
                                            {dataSource.connectionError}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={async () => {
                                    try {
                                        setRetrying(true);
                                        // Retry the workflow by triggering it again
                                        await axios.post(`/api/connectors/instances/${instanceId}/retry-setup`);
                                        toast.success("Retrying connection setup...");
                                        // Start polling for workflow status
                                        setPollingWorkflow(true);
                                        pollWorkflowStatus();
                                    } catch (error) {
                                        console.error("Failed to retry setup:", error);
                                        toast.error("Failed to retry setup. Please try again.");
                                        setRetrying(false);
                                    }
                                }}
                                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium inline-flex items-center justify-center"
                            >
                                <RefreshCw className="w-5 h-5 mr-2" />
                                Retry Setup
                            </button>
                            <button
                                onClick={() => navigate("/connectors")}
                                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium inline-flex items-center justify-center"
                            >
                                <ArrowLeft className="w-5 h-5 mr-2" />
                                Back to Connectors
                            </button>
                        </div>

                        {/* Help Text */}
                        <p className="text-sm text-gray-500 mt-6">
                            If this issue persists, please contact support with the error details above.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};
